<template>
  <div class="modal-body">
    <div class="modal-section">
      <h3 class="modal-heading">Välj vilket lån du vill ändra räntebindningen för</h3>
      <AppTable>
        <thead>
          <tr>
            <th></th>
            <th>Lånenummer</th>
            <th>Lånebelopp</th>
            <th>Nuvarande ränta</th>
            <th>Nästkommande ränta</th>
          </tr>
        </thead>
        <tbody>
          <AppTableRow v-for="loan in debt.loans" :key="loan.id" class="selectable" @click="selectedLoanId = loan.id">
            <AppTableCell action>
              <input
                class="selectable"
                type="radio"
                name="interest-rate-lock-loan"
                :checked="selectedLoanId === loan.id"
                :aria-label="`Välj lån ${loan.publicLoanNumber}`"
              />
            </AppTableCell>
            <AppTableCell nowrap>{{ loan.publicLoanNumber }}</AppTableCell>
            <AppTableCell nowrap>{{ formatAmountWithCurrency(loan.principal.current) }}</AppTableCell>
            <AppTableCell nowrap>
              {{ t(`INTEREST_TERM.${loan.interestTerm.interestTerm}`) }}, t.o.m.
              {{ formatDateIfValid(oneDayBefore(loan.interestTerm.nextInterestTermsChangeDate)) ?? '-' }} ({{
                formatPercentage(loan.pricing.interestRate)
              }})
            </AppTableCell>
            <AppTableCell nowrap>
              {{ t(`INTEREST_TERM.${loan.interestTerm.nextInterestTerm}`) }}, fr.o.m.
              {{ formatDateIfValid(loan.interestTerm.nextInterestTermsChangeDate) ?? '-' }}</AppTableCell
            >
          </AppTableRow>
        </tbody>
      </AppTable>
    </div>

    <div v-if="selectedLoan" class="loan-settings-box">
      <h3 class="modal-heading">Nya villkor för lån {{ selectedLoan.publicLoanNumber }}</h3>

      <p v-if="hasFixedRate">
        Lånet har bunden ränta t.o.m. {{ formatDateIfValid(oneDayBefore(nextChangeDate)) ?? '-' }}. Ny räntebindning och
        nya villkor kan gälla tidigast från nästa ränteändringsdag. Vill kunden ändra tidigare, kontakta Borgo via
        Meddelanden för att ta reda på om kunden behöver betala ränteskillnadsersättning (RSE) för att bryta
        bindningstiden.
      </p>

      <AppSelect v-model="newInterestTerm" label="Ny räntebindningsperiod" :options="interestTermOptions" mandatory />

      <AppSelect
        v-model="interestTermStart"
        label="Gäller från och med"
        :options="interestTermStartOptions"
        mandatory
      />

      <AppRadioGroup
        v-model="loanTermsChoice"
        name="loan-terms-choice"
        label="Rabatt & villkorsperiod"
        :options="loanTermsChoiceOptions"
        direction="row"
      />

      <template v-if="loanTermsChoice === 'new'">
        <AppTextField
          v-model="discountPercent"
          label="Rabatt (%)"
          suffix="%"
          input-mode="decimal"
          placeholder="0,25"
          :error="!!discountError"
          :error-messages="discountError"
        />
        <AppSelect
          v-model="loanTermsLength"
          label="Villkorsperiod (så länge rabatten gäller)"
          :options="loanTermsLengthOptions"
          mandatory
        />
        <AppTextField v-model="discountReason" label="Anledning till rabatt (valfritt)" :disabled="!discountBps" />
      </template>

      <p v-if="summary">{{ summary }}</p>
    </div>

    <div class="modal-actions">
      <AppButton @click="emit('close')">Avbryt</AppButton>
      <AppButton v-if="selectedLoan" color="green" :disabled="!canSubmit" :loading="isSubmitting" @click="submit">
        Spara
      </AppButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatAmountWithCurrency, formatDateIfValid, formatMonths, formatPercentage } from '@staff/shared'
import {
  AppButton,
  AppRadioGroup,
  AppSelect,
  AppTable,
  AppTableCell,
  AppTableRow,
  AppTextField,
  notify,
} from '@staff/ui'
import {
  bindLoanInterestTerm,
  fetchDebtById,
  type LoanInterestTermEffectiveFrom,
  type LoanTermsPayload,
} from '@/services/debt'
import {
  MAX_LOAN_TERMS_DISCOUNT_BPS,
  MortgageInterestTermMonths,
  MortgageInterestTerms,
  allowedLoanTermsLengths,
  isMortgageInterestTerm,
  type LoanTermsLengthInMonths,
  type MortgageInterestTerm,
} from '@/types/interest.types'
import type { Debt, Loan } from '../debt.types'

type InterestTermStart = 'tomorrow' | 'next-change-date'
type LoanTermsChoice = 'keep' | 'new'

const props = defineProps<{
  debt: Debt
  onSaved: (debt: Debt) => void
}>()

const emit = defineEmits<{
  (event: 'close'): void
}>()

const { t } = useI18n()

const selectedLoanId = ref('')
const newInterestTerm = ref<MortgageInterestTerm | ''>('')
const interestTermStart = ref<InterestTermStart>('tomorrow')
const loanTermsChoice = ref<LoanTermsChoice>('keep')
// AppSelect values are strings; parsed back to a number on submit.
const loanTermsLength = ref('')
const discountPercent = ref('')
const discountReason = ref('')
const isSubmitting = ref(false)

const tomorrow = dayjs().add(1, 'day').format('YYYY-MM-DD')

const oneDayBefore = (date: string | null) => (date ? dayjs(date).add(-1, 'day').format('YYYY-MM-DD') : null)
const formatDiscount = (bps: number) => `${formatPercentage(bps / 100)} rabatt`

const interestTermOptions = computed(() =>
  MortgageInterestTerms.map((interestTerm) => ({
    value: interestTerm,
    text: t(`INTEREST_TERM.${interestTerm}`),
  })),
)

const selectedLoan = computed(() => props.debt.loans.find((loan) => loan.id === selectedLoanId.value) ?? null)

const nextChangeDate = computed(() => selectedLoan.value?.interestTerm.nextInterestTermsChangeDate ?? null)

// A fixed rate (anything but 3 months) changes from the next change date only: breaking it earlier may cost the
// customer ränteskillnadsersättning (RSE), which Borgo handles by hand. The API rejects "tomorrow" for it.
const isFixedRate = (loan: Loan) => loan.interestTerm.interestTerm !== 'threeMonth'

const hasFixedRate = computed(() => !!selectedLoan.value && isFixedRate(selectedLoan.value))

// "Från och med imorgon" means the same thing as the next change date when that is tomorrow.
const interestTermStartOptions = computed(() => [
  ...(hasFixedRate.value || nextChangeDate.value === tomorrow
    ? []
    : [{ text: 'Från och med imorgon', value: 'tomorrow' }]),
  {
    text: `Nästa ränteändringsdag (${formatDateIfValid(nextChangeDate.value) ?? 'saknas'})`,
    value: 'next-change-date',
    disabled: !nextChangeDate.value,
  },
])

const changeDate = computed(() => (interestTermStart.value === 'tomorrow' ? tomorrow : nextChangeDate.value))

// Same-day month arithmetic, like the API does for Borgo. The API has the final say and answers 400 if it disagrees.
const newInterestTermEndDate = computed(() =>
  changeDate.value && newInterestTerm.value
    ? dayjs(changeDate.value).add(MortgageInterestTermMonths[newInterestTerm.value], 'month').format('YYYY-MM-DD')
    : null,
)

const currentLoanTerms = computed(() => selectedLoan.value?.loanTerms ?? null)

// Keeping the current loan terms is only possible when they outlast the new interest term; an interest change
// from tomorrow always starts a new loan terms period.
const canKeepLoanTerms = computed(() => {
  const loanTermsEndDate = currentLoanTerms.value?.nextLoanTermsChangeDate
  if (!loanTermsEndDate || interestTermStart.value === 'tomorrow') return false
  return !newInterestTermEndDate.value || newInterestTermEndDate.value <= loanTermsEndDate
})

const loanTermsChoiceOptions = computed(() => [
  { text: 'Behåll nuvarande', value: 'keep', disabled: !canKeepLoanTerms.value },
  { text: 'Ange ny', value: 'new' },
])

const loanTermsLengthOptions = computed(() =>
  allowedLoanTermsLengths(newInterestTerm.value).map((months) => ({
    value: String(months),
    text: formatMonths(months),
  })),
)

const discountBps = computed(() => {
  const trimmed = discountPercent.value.trim()
  if (!trimmed) return 0
  const percent = Number(trimmed.replace(',', '.'))
  return Number.isFinite(percent) ? Math.round(percent * 100) : NaN
})

const discountError = computed(() =>
  Number.isNaN(discountBps.value) || discountBps.value < 0 || discountBps.value > MAX_LOAN_TERMS_DISCOUNT_BPS
    ? `Ange en rabatt mellan 0 och ${formatPercentage(MAX_LOAN_TERMS_DISCOUNT_BPS / 100)}`
    : '',
)

const newLoanTermsValid = computed(
  () => loanTermsChoice.value === 'keep' || (!!loanTermsLength.value && !discountError.value),
)

const canSubmit = computed(
  () =>
    !!selectedLoan.value &&
    !!newInterestTerm.value &&
    !!changeDate.value &&
    (loanTermsChoice.value === 'new' || canKeepLoanTerms.value) &&
    newLoanTermsValid.value &&
    !isSubmitting.value,
)

const interestTermEffectiveFrom = computed<LoanInterestTermEffectiveFrom>(() =>
  interestTermStart.value === 'tomorrow' ? 'tomorrow' : 'nextChangeDate',
)

const summary = computed(() => {
  if (!newInterestTerm.value || !changeDate.value) return ''

  const binding = `Från ${changeDate.value} får lånet ${t(`INTEREST_TERM.${newInterestTerm.value}`)} räntebindningstid`
  if (loanTermsChoice.value === 'keep') {
    const loanTerms = currentLoanTerms.value
    const discount = loanTerms?.discounts[0]
    return `${binding}. Nuvarande villkor behålls${discount ? `: ${formatDiscount(discount.bps)} t.o.m. ${oneDayBefore(loanTerms?.nextLoanTermsChangeDate ?? null)}` : ''}.`
  }

  const lengthInMonths = Number(loanTermsLength.value)
  const period = lengthInMonths
    ? ` Rabatten gäller under nya villkorsperioden ${changeDate.value} – ${oneDayBefore(dayjs(changeDate.value).add(lengthInMonths, 'month').format('YYYY-MM-DD'))} (${formatMonths(lengthInMonths)}).`
    : ''
  return `${binding} och ${formatDiscount(Number.isNaN(discountBps.value) ? 0 : discountBps.value)}.${period}`
})

// Prefill the interest change settings for the picked loan. Loan terms already scheduled
// are shown as new terms so that "Behåll" cannot remove them by accident.
watch(selectedLoan, (loan) => {
  loanTermsChoice.value = 'keep'
  loanTermsLength.value = ''
  discountPercent.value = ''
  discountReason.value = ''

  if (!loan) {
    newInterestTerm.value = ''
    interestTermStart.value = 'tomorrow'
    return
  }

  const bookedInterestTerm = loan.interestTerm.nextInterestTerm

  if (isMortgageInterestTerm(bookedInterestTerm)) {
    newInterestTerm.value = bookedInterestTerm
  } else if (bookedInterestTerm === null) {
    newInterestTerm.value = 'threeMonth'
  } else {
    // The loan carries a term this picker can't offer (e.g. tenYear). Leave the field empty so
    // Spara stays disabled instead of silently rebinding the loan to something the user never chose.
    newInterestTerm.value = ''
  }

  interestTermStart.value =
    isFixedRate(loan) || loan.interestTerm.nextInterestTermsChangeDate ? 'next-change-date' : 'tomorrow'
  loanTermsChoice.value = canKeepLoanTerms.value ? 'keep' : 'new'

  const upcoming = loan.loanTerms.upcoming
  if (upcoming) {
    const scheduledMonths = dayjs(upcoming.endDate).diff(dayjs(upcoming.startDate), 'month')
    const closestLength = allowedLoanTermsLengths('').reduce((closest, months) =>
      Math.abs(months - scheduledMonths) < Math.abs(closest - scheduledMonths) ? months : closest,
    )
    loanTermsChoice.value = 'new'
    loanTermsLength.value = String(closestLength)
    discountPercent.value = upcoming.discounts[0] ? (upcoming.discounts[0].bps / 100).toFixed(2).replace('.', ',') : ''
    discountReason.value = upcoming.discounts[0]?.reason ?? ''
  }
})

watch(canKeepLoanTerms, (canKeep) => {
  if (!canKeep) loanTermsChoice.value = 'new'
})

// A longer interest term may rule out the picked loan terms length.
watch(loanTermsLengthOptions, (options) => {
  if (loanTermsLength.value && !options.some((option) => option.value === loanTermsLength.value))
    loanTermsLength.value = ''
})

const submit = async () => {
  const loan = selectedLoan.value
  const nextInterestTerm = newInterestTerm.value
  if (!canSubmit.value || !loan || !nextInterestTerm) return

  const loanTerms: LoanTermsPayload = {
    lengthInMonths: Number(loanTermsLength.value) as LoanTermsLengthInMonths,
    discounts:
      discountBps.value > 0
        ? [{ bps: discountBps.value, ...(discountReason.value.trim() ? { reason: discountReason.value.trim() } : {}) }]
        : [],
  }
  isSubmitting.value = true

  try {
    await bindLoanInterestTerm(props.debt.id, loan.id, {
      interestTerm: {
        nextInterestTerm,
        effectiveFrom: interestTermEffectiveFrom.value,
      },
      ...(loanTermsChoice.value === 'new' ? { loanTerms } : {}),
    })
  } catch (error) {
    notify({
      type: 'error',
      title: 'Kunde inte spara räntebindningen',
      description: error instanceof Error ? error.message : 'Ett okänt fel uppstod.',
      dismissable: true,
    })
    return
  } finally {
    isSubmitting.value = false
  }

  notify({
    type: 'success',
    title: 'Räntebindningen sparades',
  })

  // The binding already succeeded; re-read the debt only to refresh the view.
  // fetchDebtById reports its own failure, so a stale view is the worst case.
  try {
    const refreshedDebt = await fetchDebtById(props.debt.id).catch(() => null)
    if (refreshedDebt) props.onSaved(refreshedDebt)
  } catch (_err) {
    notify({
      type: 'error',
      title: 'Räntebindningen sparades men sidan kunde inte uppdateras',
      description: 'Ladda om sidan för att se den nya räntebindningen.',
      dismissable: true,
    })
  }

  emit('close')
}
</script>

<style scoped lang="scss">
// A heading inside a modal, dialled down to body size.
.modal-heading {
  font-size: var(--font-size-sm);
  line-height: var(--font-size-sm-line-height);
  font-weight: var(--font-weight-semibold);
}

.selectable {
  cursor: pointer;
}

.modal-body {
  display: flex;
  flex-direction: column;
  gap: var(--space-20);
}

.modal-section {
  display: flex;
  flex-direction: column;
  gap: var(--space-12);
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-8);
}

.loan-settings-box {
  display: flex;
  flex-direction: column;
  gap: var(--space-16);
  padding: var(--space-16);
  border: 1px solid var(--color-border-divider);
  border-radius: var(--radius-md);
}
</style>
